import React, { useEffect, useState, useCallback, useRef } from 'react';
import apiService from '../services/apiService';
import { getServerConfig } from '../config/serverConfig';
import StripeCardForm, { StripeCardFormHandle } from './StripeCardForm';

interface GateStatus {
  gate: 'none' | 'soft' | 'hard';
  grace_days_remaining: number | null;
  subscribed: boolean;
  tier: string | null;
  reason?: 'trial_expired' | 'grace_expired' | 'no_subscription';
  in_trial?: boolean;
}

interface CouponResult {
  valid: boolean;
  coupon_type: string;
  description: string;
  base_price_cents: number;
  discount_amount_cents: number;
  final_price_cents: number;
  free_months: number | null;
}

const TIERS = [
  {
    id: 'starter',
    name: 'Starter',
    price: 24.99,
    priceCents: 2499,
    description: 'For new or part-time practices',
    features: ['Up to 10 sessions/month', '1 GB storage', 'All features included'],
  },
  {
    id: 'growth',
    name: 'Growth',
    price: 39.99,
    priceCents: 3999,
    description: 'For active practices',
    features: ['Up to 30 sessions/month', '2 GB storage', 'All features included'],
    highlighted: true,
  },
  {
    id: 'elite',
    name: 'Elite',
    price: 69.99,
    priceCents: 6999,
    description: 'For high-volume practices',
    features: ['Unlimited sessions', '3 GB storage', 'All features included'],
  },
];

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export default function SubscriptionGateModal() {
  const [status, setStatus] = useState<GateStatus | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [selectedTier, setSelectedTier] = useState<string>('growth');
  const [couponCode, setCouponCode] = useState('');
  const [couponResult, setCouponResult] = useState<CouponResult | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeError, setSubscribeError] = useState<string | null>(null);
  const [billingActive, setBillingActive] = useState(false);
  const [stripeKey, setStripeKey] = useState('');
  const [agreeToPolicy, setAgreeToPolicy] = useState(false);
  const stripeRef = useRef<StripeCardFormHandle>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiService.get<GateStatus>('/billing/gate-status');
      if (res.success && res.data) {
        setStatus(res.data);
      } else {
        setStatus({ gate: 'none', grace_days_remaining: null, subscribed: false, tier: null });
      }
    } catch {
      setStatus({ gate: 'none', grace_days_remaining: null, subscribed: false, tier: null });
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    getServerConfig().then(cfg => {
      setBillingActive(cfg.billing_charging_active);
      setStripeKey(cfg.stripe_publishable_key);
    });
  }, [fetchStatus]);

  const handleDismiss = () => {
    sessionStorage.setItem('subscriptionGateDismissed', '1');
    setDismissed(true);
  };

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) return;
    setValidatingCoupon(true);
    setCouponError(null);
    setCouponResult(null);
    try {
      const res = await apiService.post<CouponResult>('/billing/validate-coupon', {
        code: couponCode.trim(),
        tier: selectedTier,
      });
      if (res.success && res.data) {
        setCouponResult(res.data);
      } else {
        setCouponError(res.error || 'Invalid coupon code');
      }
    } catch (err: any) {
      setCouponError(err?.message || 'Invalid coupon code');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleSubscribe = async () => {
    if (billingActive && !agreeToPolicy) {
      setSubscribeError('Please accept the subscription policy to continue.');
      return;
    }
    setSubscribing(true);
    setSubscribeError(null);
    try {
      if (billingActive) {
        const siRes = await apiService.post<{ client_secret: string }>('/billing/setup-intent', {});
        if (!siRes.success || !siRes.data?.client_secret) throw new Error('Failed to initialize payment');
        if (!stripeRef.current) throw new Error('Card form not ready');
        const confirmResult = await stripeRef.current.confirmSetup(siRes.data.client_secret);
        if ('error' in confirmResult) throw new Error(confirmResult.error);
        const pmRes = await apiService.post('/billing/set-default-payment-method', {
          payment_method_id: confirmResult.paymentMethodId,
        });
        if (!pmRes.success) throw new Error('Failed to save payment method');
      }
      const res = await apiService.post('/billing/subscribe', {
        tier: selectedTier,
        coupon_code: couponCode.trim() || null,
      });
      if (!res.success) throw new Error(res.error || 'Subscription failed');
      await fetchStatus();
    } catch (err: any) {
      setSubscribeError(err?.message || 'Subscription failed. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  // Reset coupon result when tier changes
  useEffect(() => {
    setCouponResult(null);
    setCouponError(null);
  }, [selectedTier]);

  if (!status || status.gate === 'none') return null;

  const wasDismissed = sessionStorage.getItem('subscriptionGateDismissed') === '1';
  if (dismissed || wasDismissed) return null;

  const selectedTierData = TIERS.find(t => t.id === selectedTier)!;
  const effectivePrice = couponResult
    ? couponResult.coupon_type === 'free_months'
      ? selectedTierData.priceCents
      : couponResult.final_price_cents
    : selectedTierData.priceCents;

  const modal = (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: status.gate === 'hard' ? 'rgba(0,0,0,0.7)' : 'transparent',
      display: 'flex', alignItems: status.gate === 'hard' ? 'center' : 'flex-start',
      justifyContent: 'center',
      pointerEvents: status.gate === 'hard' ? 'all' : 'none',
    }}>
      <div style={{
        background: '#fff',
        borderRadius: status.gate === 'hard' ? '12px' : '0 0 12px 12px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        maxWidth: status.gate === 'hard' ? '720px' : '100%',
        width: '100%',
        maxHeight: status.gate === 'hard' ? '90vh' : 'auto',
        overflowY: 'auto',
        pointerEvents: 'all',
        padding: '32px',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#111' }}>
              {status.gate === 'soft'
                ? 'Choose your CoachSight plan'
                : status.reason === 'trial_expired'
                  ? 'Your free trial has ended'
                  : status.reason === 'grace_expired'
                    ? 'Choose your CoachSight plan to continue'
                    : 'Subscription required'}
            </h2>
            <p style={{ margin: '6px 0 0', color: '#555', fontSize: '14px' }}>
              {status.gate === 'soft'
                ? `As a beta tester, you have ${status.grace_days_remaining} day${status.grace_days_remaining !== 1 ? 's' : ''} of complimentary access remaining. We appreciate your support and hope you will stay with us.`
                : status.reason === 'grace_expired'
                  ? 'Thank you for helping us build CoachSight. Your complimentary beta access has ended. Choose a plan below to keep your practice running.'
                  : status.reason === 'trial_expired'
                    ? 'Your 14-day free trial has ended. Select a plan to continue using CoachSight.'
                    : 'Select a plan to start using CoachSight.'}
            </p>
          </div>
          <button onClick={handleDismiss} style={{
            background: 'none', border: 'none', fontSize: '22px',
            cursor: 'pointer', color: '#9ca3af', padding: '0 0 0 16px', lineHeight: 1,
            flexShrink: 0,
          }} aria-label="Close">&#x2715;</button>
        </div>

        {/* Tier cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '24px' }}>
          {TIERS.map(tier => {
            const isSelected = selectedTier === tier.id;
            return (
              <button key={tier.id} onClick={() => setSelectedTier(tier.id)} style={{
                border: isSelected ? '2px solid #6d28d9' : '2px solid #e5e7eb',
                borderRadius: '10px', padding: '16px', background: isSelected ? '#f5f3ff' : '#fff',
                cursor: 'pointer', textAlign: 'left', position: 'relative',
              }}>
                {tier.highlighted && (
                  <span style={{
                    position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)',
                    background: '#6d28d9', color: '#fff', fontSize: '11px', fontWeight: 600,
                    padding: '2px 10px', borderRadius: '99px',
                  }}>Most popular</span>
                )}
                <div style={{ fontWeight: 700, fontSize: '16px', color: '#111', marginBottom: '4px' }}>{tier.name}</div>
                <div style={{ fontSize: '22px', fontWeight: 800, color: '#6d28d9', marginBottom: '6px' }}>
                  ${tier.price}<span style={{ fontSize: '13px', fontWeight: 400, color: '#666' }}>/mo</span>
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>{tier.description}</div>
                <ul style={{ margin: 0, padding: '0 0 0 16px', fontSize: '12px', color: '#444' }}>
                  {tier.features.map(f => <li key={f} style={{ marginBottom: '3px' }}>{f}</li>)}
                </ul>
              </button>
            );
          })}
        </div>

        {/* Coupon */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
            Coupon code (optional)
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              value={couponCode}
              onChange={e => { setCouponCode(e.target.value); setCouponResult(null); setCouponError(null); }}
              onKeyDown={e => e.key === 'Enter' && handleValidateCoupon()}
              placeholder="Enter code"
              style={{
                flex: 1, padding: '9px 12px', border: '1px solid #d1d5db',
                borderRadius: '6px', fontSize: '14px',
              }}
            />
            <button
              onClick={handleValidateCoupon}
              disabled={!couponCode.trim() || validatingCoupon}
              style={{
                padding: '9px 18px', background: '#6d28d9', color: '#fff',
                border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer',
                opacity: !couponCode.trim() || validatingCoupon ? 0.5 : 1,
              }}
            >
              {validatingCoupon ? 'Checking...' : 'Apply'}
            </button>
          </div>
          {couponError && (
            <p style={{ margin: '6px 0 0', color: '#dc2626', fontSize: '13px' }}>{couponError}</p>
          )}
          {couponResult && (
            <p style={{ margin: '6px 0 0', color: '#059669', fontSize: '13px', fontWeight: 500 }}>
              {couponResult.description}
              {couponResult.coupon_type !== 'free_months' && couponResult.discount_amount_cents > 0 &&
                ` — ${formatCents(couponResult.final_price_cents)}/mo after discount`}
            </p>
          )}
        </div>

        {/* Credit card — shown when billing is active */}
        {billingActive && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
              Credit Card
            </label>
            <StripeCardForm ref={stripeRef} publishableKey={stripeKey} disabled={subscribing} colorScheme="light" />
          </div>
        )}

        {/* Subscription policy checkbox */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={agreeToPolicy}
              onChange={e => setAgreeToPolicy(e.target.checked)}
              style={{ marginTop: '2px', width: '16px', height: '16px', accentColor: '#6d28d9', flexShrink: 0 }}
            />
            <span style={{ fontSize: '13px', color: '#374151', lineHeight: '1.5' }}>
              I understand I will be billed monthly based on my session count. I can cancel or change plans anytime from Settings.
            </span>
          </label>
        </div>

        {/* Price summary + Subscribe */}
        <div style={{
          background: '#f9fafb', borderRadius: '8px', padding: '16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: '16px',
        }}>
          <div>
            <div style={{ fontSize: '13px', color: '#555' }}>
              {selectedTierData.name} plan
              {couponResult?.coupon_type === 'free_months' && couponResult.free_months &&
                ` — first ${couponResult.free_months} month${couponResult.free_months !== 1 ? 's' : ''} free`}
            </div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: '#111' }}>
              {couponResult?.coupon_type === 'free_months'
                ? `${formatCents(0)}/mo for first ${couponResult.free_months} month${(couponResult.free_months ?? 1) !== 1 ? 's' : ''}`
                : `${formatCents(effectivePrice)}/mo`}
            </div>
          </div>
          <button
            onClick={handleSubscribe}
            disabled={subscribing}
            style={{
              background: '#6d28d9', color: '#fff', border: 'none',
              borderRadius: '8px', padding: '12px 28px', fontSize: '15px',
              fontWeight: 600, cursor: subscribing ? 'not-allowed' : 'pointer',
              opacity: subscribing ? 0.7 : 1,
            }}
          >
            {subscribing ? 'Activating...' : 'Start Subscription'}
          </button>
        </div>

        {subscribeError && (
          <p style={{ color: '#dc2626', fontSize: '13px', margin: '0 0 8px' }}>{subscribeError}</p>
        )}

        <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af', textAlign: 'center' }}>
          You can manage your subscription at any time from Settings.
        </p>
      </div>
    </div>
  );

  return modal;
}
